  // frontend/src/pages/Support.jsx
  import React, { useState, useEffect } from "react";
  import supportAxios from "../supportAxios";
  import axios from "../axiosConfig";
  import { useNavigate } from "react-router-dom";

  // ──────────────────────────────────────────────────────────────────────────────
  // FAQs (unchanged)
  const faqs = [
    { question: "How do I reset my password?", answer: "Go to Login → Forgot Password, enter your registered email, and follow the link we send you. The link expires after a short time for security." },
    { question: "How long do withdrawals take?", answer: "INR withdrawals usually complete within 2–3 business hours for Instant (limits apply) and within 24 business hours for Standard. Crypto withdrawals depend on network congestion." },
    { question: "Why is my crypto withdrawal fee different?", answer: "Platform fee is fixed by us; network fee varies by blockchain (gas). For USDT: TRC20 is typically lowest, ERC20 is highest, and BEP20 is moderate." },
    { question: "What are the supported networks?", answer: "BTC, ETH (ERC20), BSC (BEP20), and TRON (TRC20). Pick the network that matches your recipient address exactly." },
    { question: "How do I deposit INR?", answer: "Go to Wallet → INR Wallet → Deposit. Follow the on-screen instructions. Always use your registered name and account to avoid delays." },
    { question: "How do I deposit crypto?", answer: "Go to Wallet → Crypto Wallet → Select the coin → Deposit. Copy your deposit address for the correct network. Send only that coin to that network address." },
  ];

  export default function Support() {
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [form, setForm] = useState({ subject: "", message: "" });
    const [status, setStatus] = useState(null);
    const [openIdx, setOpenIdx] = useState(null);

    // For viewing a specific ticket
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [thread, setThread] = useState([]);
    const [threadLoading, setThreadLoading] = useState(false);
    const [showThreadModal, setShowThreadModal] = useState(false);

    // Fetch logged-in user
    useEffect(() => {
      (async () => {
        try {
          const res = await axios.get("/whoami");
          setUser(res.data.user || null);
        } catch {
          setUser(null);
        }
      })();
    }, []);

    // Load tickets
    useEffect(() => {
      (async () => {
        try {
          const res = await supportAxios.get("/tickets");
          setTickets(res.data.tickets || []);
        } catch (err) {
          console.error("Failed to load tickets:", err);
        }
      })();
    }, [status]);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!user) return setStatus({ type: "error", message: "Please sign in to submit a ticket." });
      setStatus("loading");
      try {
        const res = await supportAxios.post("/tickets", form);
        setStatus({ type: "success", ticket: res.data.ticketNumber });
        setForm({ subject: "", message: "" });
      } catch (err) {
        setStatus({ type: "error", message: err.response?.data?.error || "Submission failed." });
      }
    };

    const badgeForStatus = (s = "") => {
      const v = s.toLowerCase();
      if (v.includes("open")) return "bg-yellow-100 text-yellow-800";
      if (v.includes("pending")) return "bg-blue-100 text-blue-800";
      if (v.includes("resolved") || v.includes("closed")) return "bg-green-100 text-green-800";
      return "bg-gray-100 text-gray-800";
    };

    // ──────────────────────────────────────────────────────────────────────────────
    // View Thread Modal
    const openThread = async (ticketId) => {
      setThread([]);
      setThreadLoading(true);
      setShowThreadModal(true);
      try {
        const res = await supportAxios.get(`/tickets/${ticketId}/thread`);
        setSelectedTicket(res.data.ticket);
        setThread(res.data.ticket?.messages || []);
      } catch (err) {
        console.error("Failed to fetch thread:", err);
      } finally {
        setThreadLoading(false);
      }
    };

    const closeThread = () => {
      setSelectedTicket(null);
      setThread([]);
      setShowThreadModal(false);
    };

    // ──────────────────────────────────────────────────────────────────────────────
    return (
      <div className="min-h-screen bg-blue-50 dark:bg-black pb-10">
        <header className="bg-white dark:bg-black shadow-lg border-b-4 border-blue-600">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-bold text-blue-800 dark:text-white">Support Center</h1>
            {user && <p className="text-sm text-blue-700 dark:text-blue-300">{user.email}</p>}
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 mt-6 space-y-6">
          {/* FAQ Section */}
          <section className="bg-white dark:bg-black shadow rounded-xl p-4 border-t-4 border-blue-500">
            <h2 className="text-xl font-semibold text-blue-800 dark:text-white mb-2">FAQs</h2>
            {faqs.map((f, i) => (
              <div key={i} className="border-b border-gray-200 py-2">
                <button onClick={() => setOpenIdx(openIdx === i ? null : i)} className="w-full text-left flex justify-between">
                  <span className="font-medium text-blue-900 dark:text-white">{f.question}</span>
                  <span>{openIdx === i ? "−" : "+"}</span>
                </button>
                {openIdx === i && <div className="mt-2 text-gray-700 dark:text-gray-300 text-sm">{f.answer}</div>}
              </div>
            ))}
          </section>

          {/* Ticket History */}
          <section className="bg-white dark:bg-black shadow rounded-xl p-4 border-t-4 border-blue-500">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-xl font-semibold text-blue-800 dark:text-white">Your Tickets</h2>
            </div>

            {tickets.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-300">No tickets yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-blue-50 dark:bg-gray-900">
                      <th className="p-2 text-left">Ticket #</th>
                      <th className="p-2 text-left">Subject</th>
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((t) => (
                      <tr key={t._id} className="border-t border-gray-200">
                        <td className="p-2 font-mono">#{t.ticketNumber}</td>
                        <td className="p-2">{t.subject}</td>
                        <td className="p-2">{new Date(t.createdAt).toLocaleString()}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${badgeForStatus(t.status)}`}>{t.status}</span>
                        </td>
                        <td className="p-2">
                          <button
                            onClick={() => openThread(t._id)}
                            className="text-blue-600 hover:underline text-xs font-medium"
                          >
                            View Conversation
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* New Ticket */}
          <section className="bg-white dark:bg-black shadow rounded-xl p-4 border-t-4 border-blue-500">
            <h2 className="text-xl font-semibold text-blue-800 dark:text-white mb-3">Submit a New Ticket</h2>
            {!user ? (
              <div className="text-yellow-700 dark:text-yellow-300">Please sign in to submit a ticket.</div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="Subject"
                  className="w-full border rounded p-2"
                  required
                />
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Describe your issue"
                  rows="4"
                  className="w-full border rounded p-2"
                  required
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  {status === "loading" ? "Submitting..." : "Submit Ticket"}
                </button>
                {status?.type === "error" && <p className="text-red-600">{status.message}</p>}
                {status?.type === "success" && (
                  <p className="text-green-600">
                    Ticket #{status.ticket} created successfully. Check your email.
                  </p>
                )}
              </form>
            )}
          </section>
        </main>

        {/* Thread Modal */}
        {showThreadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3">
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-lg p-5 relative">
              <button onClick={closeThread} className="absolute top-2 right-3 text-gray-500 hover:text-gray-800">×</button>
              <h3 className="text-lg font-semibold text-blue-700 mb-3">
                Conversation for #{selectedTicket?.ticketNumber}
              </h3>
              {threadLoading ? (
                <p className="text-gray-500">Loading...</p>
              ) : (
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {thread.length === 0 ? (
                    <p className="text-gray-500">No messages yet.</p>
                  ) : (
                    thread.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`p-2 rounded ${
                          msg.sender === "admin"
                            ? "bg-blue-50 text-blue-900 ml-10"
                            : "bg-gray-100 text-gray-800 mr-10"
                        }`}
                      >
                        <div className="text-xs text-gray-500 mb-1">
                          {msg.sender === "admin" ? "Support" : "You"} •{" "}
                          {new Date(msg.timestamp).toLocaleString()}
                        </div>
                        <div className="text-sm">{msg.message}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
